import React from "react";
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia } from "@mui/material";
import { motion } from "framer-motion";

const certifications = [
    {
        title: "CompTIA A+",
        description: "Industry-standard certification for IT technical support and troubleshooting. Validates foundational IT skills including hardware, software, networking, and security.",
        image: "https://images.credly.com/size/680x680/images/63482325-a0d6-4f64-ae75-f5f33922c7d0/CompTIA_A_2Bce.png",
        issuer: "CompTIA",
    },
    {
        title: "CompTIA Network+",
        description: "Validates the essential skills needed to design, configure, manage and troubleshoot wired and wireless networks. Industry standard for network administration.",
        image: "https://images.credly.com/size/680x680/images/e1fc05b2-959b-45a4-8d20-124b1df121fe/CompTIA_Network_2Bce.png",
        issuer: "CompTIA",
    },
    {
        title: "Cisco CCNA",
        description: "Cisco Certified Network Associate certification demonstrates expertise in network fundamentals, IP connectivity, security fundamentals, and automation.",
        image: "https://images.credly.com/size/680x680/images/0a6d331e-8abf-4272-a949-33f754569a76/CCNA.png",
        issuer: "Cisco",
    },
    {
        title: "Google Cloud Associate",
        description: "Google Cloud Associate Cloud Engineer certification validates ability to deploy applications, monitor operations, and manage enterprise solutions on Google Cloud Platform.",
        image: "https://templates.images.credential.net/16590187933301617801540872729153.png",
        issuer: "Google Cloud",
    },
];

const HeroSection = () => (
    <Box
        sx={{
            position: "relative",
            height: { xs: "300px", sm: "400px", md: "500px" },
            background: "linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #facd00 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            textAlign: "center",
            padding: { xs: 2, sm: 5 },
        }}
    >
        <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
        >
            <Typography
                variant="h1"
                fontWeight="bold"
                sx={{
                    fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                }}
            >
                Professional Certifications
            </Typography>
            <Typography
                variant="h4"
                mt={2}
                sx={{
                    color: "#facd00",
                    fontSize: { xs: "1.2rem", sm: "1.8rem", md: "2.2rem" },
                }}
            >
                Industry-Recognized Expertise You Can Trust
            </Typography>
        </motion.div>
    </Box>
);

const CertificationCard = ({ cert, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
    >
        <Card
            sx={{
                boxShadow: 4,
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: 8,
                },
            }}
        >
            <Box
                sx={{
                    p: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#f5f5f5",
                    minHeight: "250px",
                }}
            >
                <CardMedia
                    component="img"
                    image={cert.image}
                    alt={cert.title}
                    sx={{
                        width: "auto",
                        maxWidth: "200px",
                        height: "auto",
                        maxHeight: "200px",
                        objectFit: "contain",
                    }}
                />
            </Box>
            <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {cert.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
                    Issued by {cert.issuer}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {cert.description}
                </Typography>
            </CardContent>
        </Card>
    </motion.div>
);

const CertificationsPage = () => {
    return (
        <>
            <HeroSection />
            <Container sx={{ py: 8 }}>
                <Box sx={{ mb: 6, textAlign: "center" }}>
                    <Typography variant="h3" fontWeight="bold" gutterBottom>
                        Our Technical Certifications
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: "900px", mx: "auto" }}>
                        Our team holds industry-leading certifications that validate our expertise in IT infrastructure,
                        networking, cloud computing, and technical support. These credentials ensure you receive
                        professional, certified service on every project.
                    </Typography>
                </Box>
                <Grid container spacing={4}>
                    {certifications.map((cert, index) => (
                        <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                            <CertificationCard cert={cert} index={index} />
                        </Grid>
                    ))}
                </Grid>

                {/* Additional Info Section */}
                <Box
                    sx={{
                        mt: 8,
                        p: 6,
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h4" fontWeight="bold" sx={{ color: "#fff", mb: 2 }}>
                        Certified Excellence in Every Project
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#facd00", mb: 2 }}>
                        Our certifications represent thousands of hours of training and real-world experience
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#fff", maxWidth: "800px", mx: "auto" }}>
                        From network infrastructure to cloud deployment, our certified technicians bring
                        validated expertise to every installation. We maintain active certifications and
                        continuously update our skills to stay current with evolving technology standards.
                    </Typography>
                </Box>
            </Container>
        </>
    );
};

export default CertificationsPage;
